import { Component, EventEmitter, HostBinding, HostListener, Input, Output, ViewChild } from '@angular/core';

@Component({
  selector: 'dx-file-dropzone',
  templateUrl: './file-dropzone.component.html',
  styleUrls: ['./file-dropzone.component.scss'],
  standalone: false,
})
export class FileDropzoneComponent {
  @Input() accept = '.csv';
  @Input() maxFileSize = 0;
  @Input() hint = '.csv files';
  @Input() uploadProgress: number | null = null;

  @Output() fileSelected = new EventEmitter<File | null>();

  @ViewChild('fileInput', { static: true }) fileInput!: { nativeElement: HTMLInputElement };

  selectedFile: File | null = null;
  isDragActive = false;

  @HostBinding('class.dx-dropzone-has-file')
  get hasFileClass(): boolean {
    return Boolean(this.selectedFile);
  }

  @HostBinding('class.dx-dropzone-drag-active')
  get dragActiveClass(): boolean {
    return this.isDragActive;
  }

  @HostBinding('class.dx-dropzone-uploading')
  get uploadingClass(): boolean {
    return this.uploadProgress !== null;
  }

  onTriggerBrowse(): void {
    if (this.uploadProgress !== null) return;
    this.fileInput?.nativeElement?.click();
  }

  onNativeFileChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    this.setFile(file);
  }

  clear(): void {
    this.selectedFile = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.fileSelected.emit(null);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (this.uploadProgress !== null) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (this.uploadProgress !== null) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (this.uploadProgress !== null) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = false;

    const file = event.dataTransfer?.files?.[0] ?? null;
    this.setFile(file);
  }

  private setFile(file: File | null): void {
    if (!file) return;

    const accepts = (this.accept ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    const lowerName = file.name.toLowerCase();
    const isAccepted =
      accepts.length === 0 ||
      accepts.some((a) => {
        if (a.startsWith('.')) return lowerName.endsWith(a.toLowerCase());
        return file.type === a;
      });

    if (!isAccepted) {
      this.clear();
      return;
    }

    if (this.maxFileSize && file.size > this.maxFileSize) {
      this.clear();
      return;
    }

    this.selectedFile = file;
    this.fileSelected.emit(file);
  }

  formatSize(bytes: number | undefined | null): string {
    const value = typeof bytes === 'number' ? bytes : 0;
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
    const num = value / Math.pow(1024, i);
    const digits = i === 0 ? 0 : 1;
    return `${num.toFixed(digits)} ${units[i]}`;
  }
}
